// @ts-nocheck - Legacy file, will be refactored

import {
	Alert,
	AlertDescription,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Switch,
} from "@ancatag/at-design";
import {
	CheckCircle2,
	Eye,
	EyeOff,
	Info,
	Loader2,
	Lock,
	Plus,
	Trash2,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { modelConfigsService } from "../../../services/models";
import type { Credentials } from "../../../services/route-configs";

// Legacy type - endpoint configuration for load balancing (not currently used)
export interface EndpointConfig {
	baseUrl: string;
	weight?: number;
}

import { toast } from "sonner";
import { cn } from "../../../lib/utils";
import type {
	GenerationType,
	ProviderTemplate,
} from "../../../services/provider-templates";

interface Step3BYOPConfigProps {
	provider: ProviderTemplate;
	isHosted: boolean;
	credentials: Credentials | null;
	endpoints?: EndpointConfig[];
	loadBalancingStrategy?:
		| "round-robin"
		| "least-connections"
		| "weighted"
		| "failover";
	generationType: GenerationType;
	draftConfigId?: string;
	onCredentialsChange: (creds: Credentials) => void;
	onEndpointsChange?: (
		endpoints: EndpointConfig[],
		strategy: "round-robin" | "least-connections" | "weighted" | "failover",
	) => void;
	onDraftConfigCreated: (configId: string) => void;
}

export function Step3BYOPConfig({
	provider,
	isHosted,
	credentials,
	endpoints: initialEndpoints,
	loadBalancingStrategy: initialStrategy,
	generationType,
	draftConfigId,
	onCredentialsChange,
	onEndpointsChange,
	onDraftConfigCreated,
}: Step3BYOPConfigProps) {
	// Mode: 'single' (legacy) or 'multiple' (new)
	const [mode, setMode] = useState<"single" | "multiple">(
		initialEndpoints && initialEndpoints.length > 0 ? "multiple" : "single",
	);

	// Single endpoint mode (legacy)
	const [baseUrl, setBaseUrl] = useState(
		credentials?.baseUrl || getDefaultBaseUrl(provider.identifier),
	);
	const [apiKey, setApiKey] = useState(credentials?.apiKey || "");
	const [orgId, setOrgId] = useState(credentials?.orgId || "");
	const [showApiKey, setShowApiKey] = useState(false);
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({});

	// Multiple endpoints mode (new)
	const [endpoints, setEndpoints] = useState<EndpointConfig[]>(
		initialEndpoints && initialEndpoints.length > 0
			? initialEndpoints
			: [
					{
						baseUrl: getDefaultBaseUrl(provider.identifier),
						apiKey: provider.identifier === "ollama" ? undefined : "",
						orgId: undefined,
						modelIdentifier: "", // Will be set in Step 4
						isActive: true,
					},
				],
	);
	const [loadBalancingStrategy, setLoadBalancingStrategy] = useState<
		"round-robin" | "least-connections" | "weighted" | "failover"
	>(initialStrategy || "round-robin");

	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState<{
		success: boolean;
		message: string;
		endpoints?: Array<{
			baseUrl: string;
			success: boolean;
			message: string;
		}>;
	} | null>(null);

	// Update parent when endpoints change
	useEffect(() => {
		if (mode === "multiple" && onEndpointsChange) {
			onEndpointsChange(endpoints, loadBalancingStrategy);
		}
	}, [endpoints, loadBalancingStrategy, mode, onEndpointsChange]);

	const handleTest = async () => {
		setTesting(true);
		setTestResult(null);

		try {
			let configId = draftConfigId;

			// Create draft config if it doesn't exist
			if (!configId) {
				const draftConfigResponse = await modelConfigsService.create({
					name: `Draft: ${provider.name} Config`,
					generationType,
					providerType: provider.identifier,
					modelIdentifier: "draft", // Placeholder, will be updated in Step 4
					isHosted,
					credentials:
						provider.identifier === "ollama"
							? { baseUrl, apiKey: undefined, orgId: undefined }
							: {
									baseUrl: baseUrl || undefined,
									apiKey,
									orgId: orgId || undefined,
								},
					isActive: false, // Draft configs are inactive
				});
				const draftConfig = draftConfigResponse.data || draftConfigResponse;
				configId = draftConfig.id;
				onDraftConfigCreated(configId);
			}

			// Test connection using backend endpoint
			const result = await modelConfigsService.testConnection(configId);

			if (result.success) {
				if (mode === "multiple" && result.endpoints) {
					// Multiple endpoints mode
					const allSuccess = result.endpoints.every((e) => e.success);
					const successCount = result.endpoints.filter((e) => e.success).length;
					setTestResult({
						success: allSuccess,
						message: `${successCount} of ${result.endpoints.length} endpoint(s) connected successfully.`,
						endpoints: result.endpoints.map((e) => ({
							baseUrl: e.baseUrl,
							success: e.success,
							message: e.success
								? `Found ${e.models.length} model(s)`
								: e.error || "Connection failed",
						})),
					});
				} else {
					// Single endpoint mode
					setTestResult({
						success: true,
						message: `Connection successful! Found ${result.models?.length || 0} model(s).`,
					});
					// Update credentials
					onCredentialsChange({
						baseUrl,
						apiKey: provider.identifier === "ollama" ? undefined : apiKey,
						orgId: provider.identifier === "ollama" ? undefined : orgId,
					});
				}
			} else {
				setTestResult({
					success: false,
					message: result.error || "Connection failed",
				});
			}
		} catch (error: any) {
			const errorMessage = error.message || "Connection test failed";
			const errorCode = error.code || "";

			// Check for specific error types
			if (errorCode === "FORBIDDEN" || errorMessage.includes("limit")) {
				toast.error("Model Config Limit Reached", {
					description:
						"You've reached the maximum number of model configurations for your plan. Please delete an existing config or upgrade your plan.",
					duration: 8000,
				});
				setTestResult({
					success: false,
					message:
						"Model config limit reached. Delete an existing config or upgrade your plan.",
				});
			} else {
				setTestResult({
					success: false,
					message: errorMessage,
				});
			}
		} finally {
			setTesting(false);
		}
	};

	const addEndpoint = () => {
		setEndpoints([
			...endpoints,
			{
				baseUrl: getDefaultBaseUrl(provider.identifier),
				apiKey: provider.identifier === "ollama" ? undefined : "",
				orgId: undefined,
				modelIdentifier: "", // Will be set in Step 4
				isActive: true,
			},
		]);
	};

	const removeEndpoint = (index: number) => {
		if (endpoints.length > 1) {
			setEndpoints(endpoints.filter((_, i) => i !== index));
		} else {
			toast.error("At least one endpoint is required");
		}
	};

	const updateEndpoint = (index: number, updates: Partial<EndpointConfig>) => {
		setEndpoints(
			endpoints.map((e, i) => (i === index ? { ...e, ...updates } : e)),
		);
	};

	const getCredentialFields = () => {
		return provider.credentialFields || [];
	};

	// Validation functions
	const validateUrl = (url: string): string | null => {
		if (!url) return null;
		try {
			const parsed = new URL(url);
			if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
				return "URL must use http:// or https://";
			}
			return null;
		} catch {
			return "Invalid URL format";
		}
	};

	const validateApiKey = (key: string, providerId: string): string | null => {
		if (providerId === "ollama") return null; // Ollama doesn't need API key
		if (!key || key.trim().length === 0) {
			return "API key is required";
		}
		if (key.length < 10) {
			return "API key appears to be invalid (too short)";
		}
		return null;
	};

	const handleBaseUrlChange = (value: string) => {
		setBaseUrl(value);
		const error = validateUrl(value);
		setValidationErrors((prev) => ({
			...prev,
			baseUrl: error || "",
		}));
	};

	const handleApiKeyChange = (value: string) => {
		setApiKey(value);
		const error = validateApiKey(value, provider.identifier);
		setValidationErrors((prev) => ({
			...prev,
			apiKey: error || "",
		}));
	};

	if (isHosted) {
		return (
			<div className="space-y-4">
				<Alert>
					<AlertDescription>
						Nova Hosted providers are coming soon. For now, please use BYOP
						(Bring Your Own Provider).
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<p className="text-sm text-muted-foreground mb-4">
					Configure your {provider.name} connection. Your credentials will be
					encrypted and stored securely.
				</p>
				<div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
					<Lock className="h-4 w-4" />
					<span>Credentials are encrypted with AES-256-GCM</span>
				</div>
			</div>

			{/* Mode Toggle */}
			<Card>
				<CardHeader>
					<CardTitle>Connection Mode</CardTitle>
					<CardDescription>
						Choose between single endpoint (legacy) or multiple endpoints with
						load balancing
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-between">
						<div className="space-y-0.5">
							<Label>Multiple Endpoints (Load Balancing)</Label>
							<p className="text-sm text-muted-foreground">
								Enable load balancing across multiple endpoints for redundancy
								and performance
							</p>
						</div>
						<Switch
							checked={mode === "multiple"}
							onCheckedChange={(checked) =>
								setMode(checked ? "multiple" : "single")
							}
						/>
					</div>
				</CardContent>
			</Card>

			{mode === "single" ? (
				/* Single Endpoint Mode (Legacy) */
				<Card>
					<CardHeader>
						<CardTitle>Connection Settings</CardTitle>
						<CardDescription>
							Enter your provider credentials to test the connection
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{provider.identifier === "ollama" ? (
							<div className="space-y-2">
								<Label htmlFor="baseUrl">Base URL</Label>
								<Input
									id="baseUrl"
									type="url"
									placeholder="http://localhost:11434"
									value={baseUrl}
									onChange={(e) => handleBaseUrlChange(e.target.value)}
									className={
										validationErrors.baseUrl ? "border-destructive" : ""
									}
								/>
								{validationErrors.baseUrl && (
									<p className="text-xs text-destructive">
										{validationErrors.baseUrl}
									</p>
								)}
								<p className="text-xs text-muted-foreground">
									{baseUrl?.includes("localhost") ||
									baseUrl?.includes("127.0.0.1") ? (
										<>
											<strong>Local Ollama detected.</strong> If Nova-route is
											hosted remotely, you'll need to expose Ollama via
											tunneling (ngrok, Cloudflare Tunnel, etc.). See{" "}
											<a
												href="https://docs.nova-route.ai/guides/local-ollama"
												target="_blank"
												rel="noopener noreferrer"
												className="text-primary hover:underline"
											>
												Local Ollama Setup Guide
											</a>
											.
										</>
									) : (
										"Default Ollama endpoint. Use localhost for local instances, or a tunnel URL (ngrok/Cloudflare) for remote Nova-route."
									)}
								</p>
							</div>
						) : (
							getCredentialFields().map((field) => {
								if (field.name === "apiKey") {
									return (
										<div key={field.name} className="space-y-2">
											<Label htmlFor={field.name}>
												{field.label}{" "}
												{field.required && (
													<span className="text-destructive">*</span>
												)}
											</Label>
											<div className="relative">
												<Input
													id={field.name}
													type={showApiKey ? "text" : "password"}
													placeholder={field.placeholder}
													value={apiKey}
													onChange={(e) => handleApiKeyChange(e.target.value)}
													required={field.required}
													className={
														validationErrors.apiKey
															? "border-destructive pr-10"
															: "pr-10"
													}
												/>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
													onClick={() => setShowApiKey(!showApiKey)}
												>
													{showApiKey ? (
														<EyeOff className="h-4 w-4 text-muted-foreground" />
													) : (
														<Eye className="h-4 w-4 text-muted-foreground" />
													)}
												</Button>
											</div>
											{validationErrors.apiKey && (
												<p className="text-xs text-destructive">
													{validationErrors.apiKey}
												</p>
											)}
											{field.helpText && !validationErrors.apiKey && (
												<p className="text-xs text-muted-foreground">
													{field.helpText}
												</p>
											)}
										</div>
									);
								}
								if (field.name === "organizationId" || field.name === "orgId") {
									return (
										<div key={field.name} className="space-y-2">
											<Label htmlFor={field.name}>
												{field.label}{" "}
												{field.required && (
													<span className="text-destructive">*</span>
												)}
											</Label>
											<Input
												id={field.name}
												type="text"
												placeholder={field.placeholder}
												value={orgId}
												onChange={(e) => setOrgId(e.target.value)}
												required={field.required}
											/>
										</div>
									);
								}
								if (field.name === "baseUrl") {
									return (
										<div key={field.name} className="space-y-2">
											<Label htmlFor={field.name}>
												{field.label}{" "}
												{field.required && (
													<span className="text-destructive">*</span>
												)}
											</Label>
											<Input
												id={field.name}
												type="url"
												placeholder={
													field.placeholder || "https://api.example.com/v1"
												}
												value={baseUrl}
												onChange={(e) => handleBaseUrlChange(e.target.value)}
												required={field.required}
												className={
													validationErrors.baseUrl ? "border-destructive" : ""
												}
											/>
											{validationErrors.baseUrl && (
												<p className="text-xs text-destructive">
													{validationErrors.baseUrl}
												</p>
											)}
										</div>
									);
								}
								return null;
							})
						)}

						<Button
							onClick={handleTest}
							disabled={
								testing ||
								(provider.identifier !== "ollama" && !apiKey) ||
								Object.keys(validationErrors).some(
									(key) => validationErrors[key] !== "",
								)
							}
							className="w-full h-10"
						>
							{testing ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Testing Connection...
								</>
							) : (
								"Test Connection"
							)}
						</Button>

						{testResult && (
							<Alert
								className={
									testResult.success ? "border-green-500" : "border-red-500"
								}
							>
								<div className="flex items-center gap-2">
									{testResult.success ? (
										<CheckCircle2 className="h-4 w-4 text-green-500" />
									) : (
										<XCircle className="h-4 w-4 text-red-500" />
									)}
									<AlertDescription>{testResult.message}</AlertDescription>
								</div>
							</Alert>
						)}
					</CardContent>
				</Card>
			) : (
				/* Multiple Endpoints Mode (New) */
				<>
					<Card>
						<CardHeader>
							<CardTitle>Load Balancing Strategy</CardTitle>
							<CardDescription>
								Choose how requests are distributed across endpoints
							</CardDescription>
						</CardHeader>
						<CardContent>
							<Select
								value={loadBalancingStrategy}
								onValueChange={(value: any) => setLoadBalancingStrategy(value)}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="round-robin">
										Round-Robin (Cycle through endpoints)
									</SelectItem>
									<SelectItem value="least-connections">
										Least-Connections (Use least busy endpoint)
									</SelectItem>
									<SelectItem value="weighted">
										Weighted (Based on endpoint weights)
									</SelectItem>
									<SelectItem value="failover">
										Failover (Try endpoints in order until one works)
									</SelectItem>
								</SelectContent>
							</Select>
							<div className="mt-3 flex items-start gap-2 p-3 bg-muted rounded-lg">
								<Info className="h-4 w-4 mt-0.5 text-muted-foreground" />
								<div className="text-xs text-muted-foreground">
									<p className="font-medium mb-1">Strategy Details:</p>
									<ul className="list-disc list-inside space-y-0.5">
										<li>
											<strong>Round-Robin:</strong> Distributes requests evenly
											across all endpoints
										</li>
										<li>
											<strong>Least-Connections:</strong> Routes to endpoint
											with fewest active connections
										</li>
										<li>
											<strong>Weighted:</strong> Uses weight values to
											distribute requests proportionally
										</li>
										<li>
											<strong>Failover:</strong> Tries endpoints sequentially
											until one succeeds
										</li>
									</ul>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle>Endpoints</CardTitle>
									<CardDescription>
										Configure multiple endpoints for load balancing. Each
										endpoint can have its own model (set in Step 4).
									</CardDescription>
								</div>
								<Button onClick={addEndpoint} variant="outline" size="sm">
									<Plus className="h-4 w-4 mr-2" />
									Add Endpoint
								</Button>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							{endpoints.map((endpoint, index) => (
								<Card key={index} className="border-2">
									<CardHeader className="pb-3">
										<div className="flex items-center justify-between">
											<CardTitle className="text-base">
												Endpoint {index + 1}
											</CardTitle>
											<div className="flex items-center gap-2">
												<div className="flex items-center gap-2">
													<Label
														htmlFor={`active-${index}`}
														className="text-sm"
													>
														Active
													</Label>
													<Switch
														id={`active-${index}`}
														checked={endpoint.isActive}
														onCheckedChange={(checked) =>
															updateEndpoint(index, { isActive: checked })
														}
													/>
												</div>
												{endpoints.length > 1 && (
													<Button
														variant="ghost"
														size="icon"
														onClick={() => removeEndpoint(index)}
														className="h-8 w-8 text-destructive"
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												)}
											</div>
										</div>
									</CardHeader>
									<CardContent className="space-y-4">
										<div className="space-y-2">
											<Label htmlFor={`baseUrl-${index}`}>
												Base URL <span className="text-destructive">*</span>
											</Label>
											<Input
												id={`baseUrl-${index}`}
												type="url"
												placeholder={getDefaultBaseUrl(provider.identifier)}
												value={endpoint.baseUrl}
												onChange={(e) =>
													updateEndpoint(index, { baseUrl: e.target.value })
												}
												required
											/>
											{endpoint.baseUrl?.includes("localhost") ||
											endpoint.baseUrl?.includes("127.0.0.1") ? (
												<p className="text-xs text-muted-foreground">
													<strong>Local endpoint detected.</strong> If
													Nova-route is hosted remotely, you'll need to expose
													this endpoint via tunneling.
												</p>
											) : null}
										</div>

										{provider.identifier !== "ollama" && (
											<>
												<div className="space-y-2">
													<Label htmlFor={`apiKey-${index}`}>API Key</Label>
													<Input
														id={`apiKey-${index}`}
														type="password"
														placeholder="sk-..."
														value={endpoint.apiKey || ""}
														onChange={(e) =>
															updateEndpoint(index, {
																apiKey: e.target.value || undefined,
															})
														}
													/>
												</div>

												{getCredentialFields().some(
													(f) =>
														f.name === "orgId" || f.name === "organizationId",
												) && (
													<div className="space-y-2">
														<Label htmlFor={`orgId-${index}`}>
															Organization ID
														</Label>
														<Input
															id={`orgId-${index}`}
															type="text"
															placeholder="org-..."
															value={endpoint.orgId || ""}
															onChange={(e) =>
																updateEndpoint(index, {
																	orgId: e.target.value || undefined,
																})
															}
														/>
													</div>
												)}
											</>
										)}

										{loadBalancingStrategy === "weighted" && (
											<div className="space-y-2">
												<Label htmlFor={`weight-${index}`}>
													Weight (0-100)
												</Label>
												<Input
													id={`weight-${index}`}
													type="number"
													min="0"
													max="100"
													placeholder="50"
													value={endpoint.weight || ""}
													onChange={(e) =>
														updateEndpoint(index, {
															weight: e.target.value
																? parseInt(e.target.value, 10)
																: undefined,
														})
													}
												/>
												<p className="text-xs text-muted-foreground">
													Higher weight = more requests routed to this endpoint
												</p>
											</div>
										)}

										<div className="text-xs text-muted-foreground p-2 bg-muted rounded">
											<Info className="h-3 w-3 inline mr-1" />
											Model selection for this endpoint will be configured in
											Step 4.
										</div>
									</CardContent>
								</Card>
							))}

							<Button
								onClick={handleTest}
								disabled={testing || endpoints.some((e) => !e.baseUrl)}
								className="w-full h-10"
							>
								{testing ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Testing Connections...
									</>
								) : (
									"Test All Endpoints"
								)}
							</Button>

							{testResult && (
								<Alert
									className={
										testResult.success
											? "border-green-500"
											: "border-yellow-500"
									}
								>
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											{testResult.success ? (
												<CheckCircle2 className="h-4 w-4 text-green-500" />
											) : (
												<XCircle className="h-4 w-4 text-yellow-500" />
											)}
											<AlertDescription className="font-medium">
												{testResult.message}
											</AlertDescription>
										</div>
										{testResult.endpoints && (
											<div className="space-y-2 mt-3">
												{testResult.endpoints.map((ep, idx) => (
													<div
														key={idx}
														className={cn(
															"flex items-center gap-2 p-2 rounded text-sm",
															ep.success
																? "bg-green-500/10 text-green-700 dark:text-green-400"
																: "bg-red-500/10 text-red-700 dark:text-red-400",
														)}
													>
														{ep.success ? (
															<CheckCircle2 className="h-4 w-4" />
														) : (
															<XCircle className="h-4 w-4" />
														)}
														<span className="font-mono text-xs">
															{ep.baseUrl}
														</span>
														<span className="ml-auto">{ep.message}</span>
													</div>
												))}
											</div>
										)}
									</div>
								</Alert>
							)}
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}

function getDefaultBaseUrl(providerIdentifier: string): string {
	const defaults: Record<string, string> = {
		ollama: "http://localhost:11434",
		openai: "https://api.openai.com/v1",
		anthropic: "https://api.anthropic.com/v1",
	};
	return defaults[providerIdentifier] || "";
}
